import React from "react";
import { formatCurrency } from "utils/currency";
import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";
import StatusBadge from "components/common/StatusBadge";
import { DateOnly, DateTime } from "components/common/Date";
import { Quotation } from "features/Quotations/types/quotation.types";
import {
  QuotationStatus,
  QuotationSourceLabelMap,
  QuotationSourceType,
  QuotationStatusMap,
} from "features/Quotations/constants/quotation.constants";
import { clientLine } from "./quotationOverview.utils";
import { ClientInfoCard } from "components/common/ClientInfoCard";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";

type Props = {
  record: Quotation;
  sourcePath: string;
};

function pdfTermsNumeric(
  record: Quotation,
  camel: "advancePaymentPercent" | "workCompletionWeeks",
  snake: string,
): number | null {
  const row = record as unknown as Record<string, unknown>;
  const raw = row[camel] ?? row[snake];
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

export default function QuotationOverviewTab({ record, sourcePath }: Props) {
  const advancePct = pdfTermsNumeric(
    record,
    "advancePaymentPercent",
    "advance_payment_percent",
  );
  const workWeeks = pdfTermsNumeric(
    record,
    "workCompletionWeeks",
    "work_completion_weeks",
  );
  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <h6 className="mb-3 text-uppercase">Quotation info</h6>
            <div className="table-responsive">
              <Table className="table-borderless mb-0">
                <tbody>
                  {record.code ? (
                    <tr>
                      <th
                        className="ps-0"
                        scope="row"
                        style={{ width: "200px" }}
                      >
                        Code
                      </th>
                      <td className="text-muted">{record.code}</td>
                    </tr>
                  ) : null}
                  {record.total != null ? (
                    <tr>
                      <th className="ps-0" scope="row">
                        Total
                      </th>
                      <td className="fw-medium">{formatCurrency(record.total)}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <th className="ps-0" scope="row">
                      Title
                    </th>
                    <td className="text-muted">
                      {record.title?.trim() || "—"}
                    </td>
                  </tr>
                  <tr>
                    <th className="ps-0" scope="row">
                      Source
                    </th>
                    <td>
                      {record.sourceType ? (
                        <div>
                          <div className="text-muted small">
                            {QuotationSourceLabelMap[
                              record.sourceType as QuotationSourceType
                            ] ?? record.sourceType}
                          </div>
                          {sourcePath && record.source?.code ? (
                            <Link
                              to={`${sourcePath}/${record.sourceId}`}
                              className="link fw-medium"
                            >
                              {record.source.code}
                            </Link>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th className="ps-0" scope="row">
                      Client
                    </th>
                    <td className="text-muted">{clientLine(record)}</td>
                  </tr>
                  <tr>
                    <th className="ps-0" scope="row">
                      Valid until
                    </th>
                    <td className="text-muted">
                      <DateOnly date={record.validUntil} />
                    </td>
                  </tr>
                  <tr>
                    <th className="ps-0" scope="row">
                      Est. start
                    </th>
                    <td className="text-muted">
                      <DateOnly date={record.estimatedStartDate} />
                    </td>
                  </tr>
                  <tr>
                    <th className="ps-0" scope="row">
                      Est. end
                    </th>
                    <td className="text-muted">
                      <DateOnly date={record.estimatedEndDate} />
                    </td>
                  </tr>
                  {advancePct != null ? (
                    <tr>
                      <th className="ps-0" scope="row">
                        Advance payment (PDF)
                      </th>
                      <td className="text-muted">{advancePct}%</td>
                    </tr>
                  ) : null}
                  {workWeeks != null ? (
                    <tr>
                      <th className="ps-0" scope="row">
                        Work completion (PDF)
                      </th>
                      <td className="text-muted">{workWeeks} weeks</td>
                    </tr>
                  ) : null}
                  {record.assignedTo != null && record.assignedUser ? (
                    <tr>
                      <th className="ps-0" scope="row">
                        Assigned to
                      </th>
                      <td className="text-muted">
                        <Link
                          to={`/user-management/users/${record.assignedTo}`}
                          className="link fw-medium"
                        >
                          {[
                            record.assignedUser.firstName,
                            record.assignedUser.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ") || "—"}
                        </Link>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>

            {record.description?.trim() ? (
              <div className="pt-3 mt-4">
                <h6 className="mb-3 text-uppercase">Notes</h6>
                <p className="text-muted mb-0">{record.description}</p>
              </div>
            ) : null}

            <div className="pt-3 border-top border-top-dashed mt-4">
              <Row className="gy-3">
                {record.created_at ? (
                  <Col lg={3} sm={6}>
                    <p className="mb-2 text-uppercase fw-medium small">
                      Created
                    </p>
                    <h5 className="fs-14 mb-0">
                      <DateTime date={record.created_at} />
                    </h5>
                  </Col>
                ) : null}
                {record.updated_at ? (
                  <Col lg={3} sm={6}>
                    <p className="mb-2 text-uppercase fw-medium small">
                      Last updated
                    </p>
                    <h5 className="fs-14 mb-0">
                      <DateTime date={record.updated_at} />
                    </h5>
                  </Col>
                ) : null}
                <Col lg={3} sm={6}>
                  <p className="mb-2 text-uppercase fw-medium small">Status</p>
                  <h5 className="fs-14 mb-0">
                    <StatusBadge
                      value={record.status}
                      map={QuotationStatusMap}
                    />
                  </h5>
                </Col>
              </Row>
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col lg={4}>
        <Card className="mb-3">
          <CardBody>
            <h6 className="text-uppercase mb-4 text-muted">Timeline</h6>
            <Timeline steps={buildQuotationTimeline(record)} />
          </CardBody>
        </Card>
        {record.client && record.clientId ? (
          <ClientInfoCard client={record.client} clientId={record.clientId} />
        ) : null}
      </Col>
    </Row>
  );
}

const QUOTATION_SKIPPED = new Set<string>([
  QuotationStatus.REJECTED,
  QuotationStatus.EXPIRED,
  QuotationStatus.CANCELLED,
]);

function buildQuotationTimeline(record: Quotation): TimelineStep[] {
  const history = record.statusHistory ?? [];
  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const label = QuotationStatusMap[entry.status as QuotationStatus]?.label ?? entry.status;
    const state: TimelineStepState = isLast
      ? QUOTATION_SKIPPED.has(entry.status)
        ? "skipped"
        : entry.status === QuotationStatus.ACCEPTED
          ? "done"
          : "active"
      : "done";
    const byLabel = entry.user
      ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
      : entry.by != null ? `#${entry.by}` : undefined;
    return { label, timestamp: entry.at, by: byLabel, actor: entry.message ?? undefined, state };
  });
}
